import { ComponentType, ReactElement, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { noop } from "lodash"
import BreakdownTooltipComponent from "components/breakdown-tooltip"
import MultiLineBreakdownComponent from "components/charts/multi-line-breakdown"
import LegendPanelComponent from "renderers/multi-line-graph/components/legend-panel"
import { getLatestItemValues, getLatestItemDates } from "renderers/multi-line-graph/helpers"
import { GraphLoadingState, GraphEmptyState } from "renderers/multi-line-graph/components/graph-states"
import GraphErrorFallback from "renderers/multi-line-graph/components/graph-error-fallback"
import ChartNotice from "renderers/multi-line-graph/components/chart-notice"
import { smallPercentageFormatter } from "helpers/formatters/numbers"
import { useMultiLineHandlers } from "renderers/multi-line-graph/hooks/use-multiline-handlers"
import { LOADING, ERROR, OK } from "helpers/data-statuses"
import { MULTI_LINE_ITEM_SHAPE } from "renderers/multi-line-graph/prop-types"
import { TOTAL_RISK, FACTORS_RISK, SPECIFIC_RISK } from "gatherers/multi-date/risk"
import { ATTRIBUTION_AREAS } from "helpers/analyzeConstants"
import { ErrorBoundary } from "@sentry/react"
import multiDateContract from "gatherers/multi-date/contract"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import { IntradayChartMode } from "connected-components/dashboard/hooks/use-intraday-state"

interface BreakdownTooltipProps {
  items: RiskItem[]
  formatter?: (value: number) => string
  totalKey?: string
  datum?: { date: string, [key: string]: unknown }
  formatters?: Record<string, (value: number) => string>
  intradayChartMode?: IntradayChartMode
}

interface MultiLineBreakdownProps {
  data: object[]
  items: RiskItem[]
  startDate: string
  endDate: string
  setDate: (date: string) => void
  toggle: (id: string) => void
  toggleOnly: (id: string, legendText: string) => void
  focusChartLine: (id: string) => void
  blurChartLine: () => void
  focusedChartLine: string | null
  onlyItem: boolean
  totalLabel?: string
  breakdownLabel?: string
  tickFormat?: (value: number) => string
  tooltip: ReactElement
  onDotClick?: (position: ClickPosition, focusedChartLineId: string) => void
  isOneDayMode?: boolean
  hideLegend?: boolean
}

interface LegendPanelProps {
  items: RiskItem[]
  visibleCount: number
  latestValues: Record<string, number>
  latestDates?: Record<string, string>
  formatter: (value: number) => string
  onlyLegendText: string
  onClick: (id: string, defaultDisabled?: boolean) => void
  onOnlyToggle: (id: string, legendText: string) => void
  focus: (id: string) => void
  blur: () => void
  focused: string | null
  addSeriesDisabledReason: string
  sortable: boolean
}

const BreakdownTooltip = BreakdownTooltipComponent as ComponentType<BreakdownTooltipProps>
const MultiLineBreakdown = MultiLineBreakdownComponent as ComponentType<MultiLineBreakdownProps>
const LegendPanel = LegendPanelComponent as ComponentType<LegendPanelProps>

const ADD_SERIES_UNAVAILABLE_REASON = "Add series isn't available on the Total view"

// Maps from the multi-date gatherer IDs to the IDs expected by MultiLineBreakdown.
// MultiLineBreakdown identifies the total line by id === "total" and renders all
// other items as stacked areas on the right axis.
const GATHERER_TO_CHART_ID: Record<string, string> = {
  [TOTAL_RISK]: "total",
  [FACTORS_RISK]: "factor",
  [SPECIFIC_RISK]: "specific",
}

const CHART_TO_GATHERER_ID: Record<string, string> = Object.fromEntries(
  Object.entries(GATHERER_TO_CHART_ID).map(([gathererKey, chartKey]) => [chartKey, gathererKey]),
)

const ITEM_AREA_COLOR: Record<string, string> = {
  [FACTORS_RISK]: ATTRIBUTION_AREAS.FACTOR,
  [SPECIFIC_RISK]: ATTRIBUTION_AREAS.ALPHA,
}

interface RiskItem {
  id: string
  label: string
  color: string
  disabled: boolean
  type: string
  area?: string
}

interface MultiLineHandlers {
  toggle: (id: string) => void
  toggleOnly: (id: string, legendText: string) => void
  focusChartLine: (id: string) => void
  blurChartLine: () => void
  handleSetDate: (date: string) => void
  isOnlyMode: boolean
  onlyLegendText: string
  focusedChartLine: string | null
}

interface RiskSummaryChartComponentProps {
  data: object[]
  items: RiskItem[]
  startDate: string
  endDate: string
  setItems?: (id: string, defaultDisabled: boolean) => void
  setOnlyItem?: (id: string, allIds: string[]) => void
  enableItems?: (ids: string[]) => void
  onDotClick?: (position: ClickPosition, focusedItemId: string) => void
  emptyMessage?: string
  noDataMessage?: string
  isOneDayMode?: boolean
  intradayChartMode?: IntradayChartMode
  isTruncated?: boolean
  hasFailedBatches?: boolean
}

function RiskSummaryChartComponent({
  data,
  items = [],
  startDate,
  endDate,
  setItems = noop,
  setOnlyItem = noop,
  enableItems = noop,
  onDotClick,
  emptyMessage,
  noDataMessage,
  isOneDayMode = false,
  intradayChartMode = "none",
  isTruncated = false,
  hasFailedBatches = false,
}: RiskSummaryChartComponentProps) {
  const breakdownItems = useMemo(
    () => items.map(item => ({
      ...item,
      id: GATHERER_TO_CHART_ID[item.id] ?? item.id,
      area: ITEM_AREA_COLOR[item.id],
    })),
    [items],
  )

  const breakdownData = useMemo(
    () => (data as Record<string, unknown>[]).map(datum => ({
      ...datum,
      total: datum[TOTAL_RISK],
      factor: datum[FACTORS_RISK],
      specific: datum[SPECIFIC_RISK],
    })),
    [data],
  )

  const wrappedSetItems = useCallback(
    (id: string, defaultDisabled: boolean) => setItems(CHART_TO_GATHERER_ID[id] ?? id, defaultDisabled),
    [setItems],
  )

  const wrappedSetOnlyItem = useCallback(
    (id: string, allIds: string[]) => setOnlyItem(
      CHART_TO_GATHERER_ID[id] ?? id,
      allIds.map(chartId => CHART_TO_GATHERER_ID[chartId] ?? chartId),
    ),
    [setOnlyItem],
  )

  const wrappedEnableItems = useCallback(
    (ids: string[]) => enableItems(ids.map(chartId => CHART_TO_GATHERER_ID[chartId] ?? chartId)),
    [enableItems],
  )

  const {
    toggle,
    toggleOnly,
    focusChartLine,
    blurChartLine,
    handleSetDate,
    isOnlyMode,
    onlyLegendText,
    focusedChartLine,
  } = useMultiLineHandlers(breakdownItems, wrappedSetItems, wrappedSetOnlyItem, wrappedEnableItems, noop) as MultiLineHandlers

  const latestValues = useMemo(() => getLatestItemValues(breakdownData, breakdownItems) as Record<string, number>, [breakdownData, breakdownItems])
  const latestDates = useMemo(() => getLatestItemDates(breakdownData, breakdownItems) as Record<string, string>, [breakdownData, breakdownItems])
  const visibleCount = useMemo(() => breakdownItems.filter(item => !item.disabled).length, [breakdownItems])

  if (!data || !data.length) {
    return <GraphEmptyState message={noDataMessage ?? emptyMessage} />
  }

  if (!breakdownItems.length) {
    return <GraphEmptyState message={emptyMessage} />
  }

  return (
    <div className="graph-content">
      <ChartNotice isTruncated={isTruncated} hasFailedBatches={hasFailedBatches} historicalOnlyMessage={undefined} />
      <div className="enhanced-multi-line-container legend-placement-right">
        <div className="graph-body">
          <div className="graph-chart-area">
            <MultiLineBreakdown
              data={breakdownData}
              items={breakdownItems}
              startDate={startDate}
              endDate={endDate}
              setDate={handleSetDate}
              toggle={toggle}
              toggleOnly={toggleOnly}
              focusChartLine={focusChartLine}
              blurChartLine={blurChartLine}
              focusedChartLine={focusedChartLine}
              onlyItem={isOnlyMode}
              totalLabel="Total Risk"
              breakdownLabel="Risk Decomposition"
              tickFormat={smallPercentageFormatter}
              onDotClick={onDotClick}
              isOneDayMode={isOneDayMode}
              hideLegend
              tooltip={
                <BreakdownTooltip
                  items={breakdownItems}
                  formatter={smallPercentageFormatter}
                  totalKey="total"
                  intradayChartMode={intradayChartMode}
                />
              }
            />
          </div>
          <LegendPanel
            items={breakdownItems}
            visibleCount={visibleCount}
            latestValues={latestValues}
            latestDates={latestDates}
            formatter={smallPercentageFormatter}
            onlyLegendText={onlyLegendText}
            onClick={toggle}
            onOnlyToggle={toggleOnly}
            focus={focusChartLine}
            blur={blurChartLine}
            focused={focusedChartLine}
            addSeriesDisabledReason={ADD_SERIES_UNAVAILABLE_REASON}
            sortable={false}
          />
        </div>
      </div>
    </div>
  )
}

RiskSummaryChartComponent.propTypes = {
  data: multiDateContract.isRequired,
  items: PropTypes.arrayOf(MULTI_LINE_ITEM_SHAPE).isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  setItems: PropTypes.func,
  setOnlyItem: PropTypes.func,
  enableItems: PropTypes.func,
  onDotClick: PropTypes.func,
  emptyMessage: PropTypes.string,
  noDataMessage: PropTypes.string,
  isOneDayMode: PropTypes.bool,
  intradayChartMode: PropTypes.oneOf(["none", "liveTick", "batchTrend"]),
  isTruncated: PropTypes.bool,
  hasFailedBatches: PropTypes.bool,
}

interface RiskSummaryChartWithErrorProps extends RiskSummaryChartComponentProps {
  status?: string
  errorMessage?: string
}

function RiskSummaryChartWithError({ status, errorMessage, ...props }: RiskSummaryChartWithErrorProps) {
  if (status === ERROR) {
    throw new Error(errorMessage)
  }
  return <RiskSummaryChartComponent {...props} />
}

RiskSummaryChartWithError.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, OK]),
  errorMessage: PropTypes.string,
  onDotClick: PropTypes.func,
  emptyMessage: PropTypes.string,
  noDataMessage: PropTypes.string,
  isOneDayMode: PropTypes.bool,
  intradayChartMode: PropTypes.oneOf(["none", "liveTick", "batchTrend"]),
  isTruncated: PropTypes.bool,
  hasFailedBatches: PropTypes.bool,
}

interface RiskSummaryChartProps extends RiskSummaryChartComponentProps {
  status?: string
  loadingMessage?: string
  errorMessage?: string
}

export default function RiskSummaryChart({ status, loadingMessage, ...props }: RiskSummaryChartProps) {
  if (status === LOADING) {
    return <GraphLoadingState message={loadingMessage} />
  }

  return (
    <ErrorBoundary fallback={GraphErrorFallback}>
      <RiskSummaryChartWithError status={status} {...props} />
    </ErrorBoundary>
  )
}

RiskSummaryChart.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, OK]),
  data: multiDateContract,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(MULTI_LINE_ITEM_SHAPE).isRequired,
  setItems: PropTypes.func,
  setOnlyItem: PropTypes.func,
  enableItems: PropTypes.func,
  onDotClick: PropTypes.func,
  loadingMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
  noDataMessage: PropTypes.string,
  isOneDayMode: PropTypes.bool,
  intradayChartMode: PropTypes.oneOf(["none", "liveTick", "batchTrend"]),
  isTruncated: PropTypes.bool,
  hasFailedBatches: PropTypes.bool,
}
